/**
 * bomStandardization.ts
 *
 * Client API service for BOM Review and Batch Name Standardization.
 */
import { apiFetch } from '@/services/apiFetch'

export type BomType = 'WOOD' | 'HARDWARE' | 'FOAM'
export type BomFieldName = 'wood_type_raw' | 'name_raw' | 'loai'
export type ConfidenceLevel = 'CONFIDENT' | 'NEEDS_REVIEW' | 'UNKNOWN'
export type PriceImpact = 'UNCHANGED' | 'MATCHED_NEW' | 'PRICE_CHANGED' | 'UNMATCHED'

export interface BomReviewLineProposal {
  id: string
  bom_type: BomType
  config_id: string
  config_code: string
  product_id: string
  product_code: string
  product_name: string
  line_id: string
  field_name: BomFieldName
  current_name: string
  proposed_name: string
  standard_name_id: string | null
  confidence: ConfidenceLevel
  confidence_reason: string
  spec: string | null
  proposed_spec?: string | null
  color?: string | null
  proposed_color?: string | null
  uom: string | null
  qty_per: number | null
  price_old: number | null
  price_new: number | null
  price_impact: PriceImpact
  price_warning?: string | null
  selected: boolean
}

export interface BomReviewGroupSummary {
  old_name: string
  proposed_name: string
  standard_name_id: string | null
  bom_type: BomType
  confidence: ConfidenceLevel
  total_lines: number
  total_products: number
  price_impact: PriceImpact
  sample_spec: string | null
}

export interface BomReviewResult {
  lines: BomReviewLineProposal[]
  groups: BomReviewGroupSummary[]
  stats: {
    total_lines: number
    confident_count: number
    needs_review_count: number
    unknown_count: number
    affected_products_count: number
  }
}

export interface BomNameChangeBatch {
  id: string
  batch_name: string
  total_lines: number
  applied_lines: number
  reverted: boolean
  reverted_at: string | null
  reverted_by: string | null
  created_by: string
  created_at: string
  notes: string | null
}

export interface BomNameChangeLine {
  id: string
  batch_id: string
  bom_type: BomType
  config_id: string
  product_id: string
  line_id: string
  field_name: BomFieldName
  old_name: string
  new_name: string
  spec: string | null
  old_spec?: string | null
  new_spec?: string | null
  old_color?: string | null
  new_color?: string | null
  reverted: boolean
  revert_skipped_reason: string | null
  created_at: string
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const res = await apiFetch(input, init)
  const data = await res.json()
  if (!res.ok) {
    const err = data as { error?: { message?: string } }
    throw new Error(err?.error?.message || `Lỗi HTTP ${res.status}`)
  }
  return data as T
}

export const bomStandardizationService = {
  async review(params?: {
    productId?: string
    bomType?: string
    confidence?: string
  }): Promise<BomReviewResult> {
    const q = new URLSearchParams()
    if (params?.productId) q.set('productId', params.productId)
    if (params?.bomType) q.set('bomType', params.bomType)
    if (params?.confidence) q.set('confidence', params.confidence)

    const res = await fetchJson<{ ok: boolean } & BomReviewResult>(
      `/api/boms/review-names${q.toString() ? `?${q.toString()}` : ''}`,
    )
    return {
      lines: res.lines,
      groups: res.groups,
      stats: res.stats,
    }
  },

  async applyChanges(data: {
    batch_name?: string
    lines: Array<{
      bom_type: BomType
      config_id: string
      product_id: string
      line_id: string
      field_name: BomFieldName
      old_name: string
      new_name: string
      spec?: string | null
      old_spec?: string | null
      new_spec?: string | null
      old_color?: string | null
      new_color?: string | null
    }>
    notes?: string
  }): Promise<{ batch: BomNameChangeBatch; applied_lines_count: number }> {
    const res = await fetchJson<{
      ok: boolean
      batch: BomNameChangeBatch
      applied_lines_count: number
    }>('/api/boms/apply-name-changes', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(data),
    })
    return {
      batch: res.batch,
      applied_lines_count: res.applied_lines_count,
    }
  },

  async listBatches(limit = 50): Promise<BomNameChangeBatch[]> {
    const res = await fetchJson<{ ok: boolean; batches: BomNameChangeBatch[] }>(
      `/api/boms/name-change-batches?limit=${limit}`,
    )
    return res.batches
  },

  async getBatchLines(batchId: string): Promise<BomNameChangeLine[]> {
    const res = await fetchJson<{ ok: boolean; lines: BomNameChangeLine[] }>(
      `/api/boms/name-change-batches/${batchId}/lines`,
    )
    return res.lines
  },

  async revertBatch(batchId: string): Promise<{
    batch: BomNameChangeBatch
    reverted_count: number
    skipped_count: number
  }> {
    const res = await fetchJson<{
      ok: boolean
      batch: BomNameChangeBatch
      reverted_count: number
      skipped_count: number
    }>(`/api/boms/revert-name-change-batch/${batchId}`, {
      method: 'POST',
    })
    return {
      batch: res.batch,
      reverted_count: res.reverted_count,
      skipped_count: res.skipped_count,
    }
  },
}
